import { UserName } from "@/components/UserName";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LoadMore } from "@/components/LoadMore";
import { formatDateTime } from "@/lib/datetime";
import { Search, RotateCcw } from "lucide-react";

const PAGE = 100;

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const didInit = useRef(false);

  // server-side fetch — filters + pagination done in the DB (scales past 500 rows)
  const fetchPage = useCallback(async (p, replace) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/audit-logs", {
        params: { page: p, page_size: PAGE, q, action, date_from: from, date_to: to },
      });
      setLogs((prev) => (replace ? data.items : [...prev, ...data.items]));
      setTotal(data.total);
      if (data.actions?.length) setActions(data.actions);
      setPage(p);
    } finally { setLoading(false); }
  }, [q, action, from, to]);

  // refetch page 1 whenever filters change (debounced for the search box)
  useEffect(() => {
    const delay = didInit.current ? 300 : 0;
    didInit.current = true;
    const t = setTimeout(() => fetchPage(1, true), delay);
    return () => clearTimeout(t);
  }, [fetchPage]);

  const hasFilters = q || action || from || to;
  const clear = () => { setQ(""); setAction(""); setFrom(""); setTo(""); };
  const hasMore = logs.length < total;

  return (
    <div data-testid="audit-page">
      <h1 className="font-display text-2xl font-bold tracking-tight">Audit Log</h1>
      <p className="text-sm text-slate-500 mb-4">Append-only · who / what / when / before / after · not editable</p>

      <div className="flex flex-wrap gap-2 items-end mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search actor, action, entity…" className="pl-8 h-9" autoComplete="off" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Action</label>
          <select data-testid="audit-action-filter" value={action} onChange={(e) => setAction(e.target.value)}
            className="rounded-lg border border-slate-200 text-sm px-2 h-9 bg-white max-w-[200px]">
            <option value="">All actions</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">From</label>
          <Input type="date" data-testid="audit-from" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40 h-9" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">To</label>
          <Input type="date" data-testid="audit-to" value={to} onChange={(e) => setTo(e.target.value)} className="w-40 h-9" />
        </div>
        {hasFilters && (
          <Button data-testid="audit-clear" variant="outline" onClick={clear} className="h-9 rounded-lg border-slate-300">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Clear
          </Button>
        )}
      </div>

      <div className="border border-slate-200 bg-white rounded-lg overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="bg-slate-100 text-left uppercase tracking-wider text-[10px] text-slate-500">
              <th className="py-2 px-3 border-r border-slate-200">Time (yard)</th>
              <th className="py-2 px-3 border-r border-slate-200">Actor</th>
              <th className="py-2 px-3 border-r border-slate-200">Action</th>
              <th className="py-2 px-3 border-r border-slate-200">Entity</th>
              <th className="py-2 px-3">Change</th>
            </tr>
          </thead>
          <tbody data-testid="audit-table-body">
            {logs.map((l) => (
              <tr key={l.id} className="log-row border-t border-slate-200 align-top">
                <td className="py-1 px-3 border-r border-slate-100 whitespace-nowrap">{formatDateTime(l.timestamp)}</td>
                <td className="py-1 px-3 border-r border-slate-100"><UserName email={l.actor_email} inline /></td>
                <td className="py-1 px-3 border-r border-slate-100 text-[#B01E28]">{l.action}</td>
                <td className="py-1 px-3 border-r border-slate-100">{l.entity_type}</td>
                <td className="py-1 px-3 max-w-xs truncate text-slate-500" title={JSON.stringify({ before: l.before, after: l.after })}>
                  {l.after ? JSON.stringify(l.after).slice(0, 80) : "—"}
                </td>
              </tr>
            ))}
            {total === 0 && !loading && <tr><td colSpan={5} className="py-6 text-center text-slate-400" data-testid="audit-empty">No audit entries match.</td></tr>}
          </tbody>
        </table>
      </div>

      <LoadMore shownCount={logs.length} total={total} hasMore={hasMore} onMore={() => fetchPage(page + 1, false)} noun="entry" nounPlural="entries" testid="audit-load-more" />
    </div>
  );
}
